import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { GetWarehousesApiParameter } from "@insite/client-framework/Services/WarehouseService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Profile from "@store/Components/Icons/Profile";
import ProfileMobile from "@store/Components/Icons/ProfileMobile";
import LoginButtonCheckboxDisplay from "@store/Components/Store/LoginButtonCheckboxDisplay";
import AccountListMenu from "@store/Components/Store/MyAccount/AccountListMenu";
import usePreviousExisting from "@store/Hooks/usePreviousExisting";
import AddressInfoDisplay from "@store/Overrides/Components/AddressInfoDisplay";
import FindLocationModal from "@store/Overrides/Components/FindLocationModal";
import { SignInExternalProvidersPartialProps } from "@store/Overrides/Widgets/SignIn/SignInExternalProviders";
import { getStoreSession, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import updateWarehouse from "@store/Store/Context/Warehouses/Handlers/UpdateWarehouse";
import loadRetailModeSettings from "@store/Store/Data/RetailModeSettings/Handlers/LoadRetailModeSettings";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { contentSpacingStyles, focusStyles, formVerticalRadioGroupStyles, gridGapStyles } from "@store/theme/common/mixins";
import { getLoadingOverlayProps } from "@store/theme/common/props/loadingOverlay";
import { MENU, TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import isTheme from "@store/utilities/isTheme";
import * as React from "react";
import { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const SignInExternalProviders = getWidgetComponent("SignIn/SignInExternalProviders");

export interface AccountListModel {
    fields: {
        linkName: string;
        categoryTitle: string;
        isCategoryTitle: string;
        showOnlyAdmin: boolean;
        showOnlyAdminBuyer3: boolean;
        hideForCanadianCustomers: boolean;
        link: LinkFieldValue;
    }
}

const enum fields {
    links = "links",
	nodeId = "nodeId",
    showRootPage = "showRootPage",
    showSignOut = "showSignOut",
}

interface OwnProps extends WidgetProps{
    fields: {
        [fields.links]: AccountListModel[];
        [fields.nodeId]: LinkFieldValue;
        [fields.showRootPage]: boolean;
        [fields.showSignOut]: boolean;
    };
}

const mapStateToProps = (state: StoreApplicationState) => {
    const settings = getStoreSettingsCollection(state);
    const session = getStoreSession(state);
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    const warehousesParameter: GetWarehousesApiParameter = {
        search: "",
        latitude: 0,
        longitude: 0,
        pageSize: 1000,
        onlyPickupWarehouses: true,
    };

    return {
        cart,
        userName: state.context.session.userName,
        firstName: state.context.session.firstName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
		rememberMe: state.context.session.rememberMe,
        currentBillTo: getCurrentBillToState(state).value,
        currentShipTo: getCurrentShipToState(state).value,
        shipToState: getShipToState(state, cart?.shipToId),
        showPickUpOption: settings.accountSettings.enableWarehousePickup,
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        allowCreateAccount: settings.accountSettings.allowCreateAccount,
        allowInternalSignIn: settings.poolcorpSettings.allowInternalSignIn,
        allowPickupBranchSelection: !settings.poolcorpSettings.disablePickupBranchSelectionByCountry,
        isFullfillmentMethodPickup: session.fulfillmentMethod === FulfillmentMethod.PickUp,
        currentWarehouseName:
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.name
                : session.warehouse.name) || '',
        disallowedShippingBranches: settings.poolcorpSettings.disallowedShippingBranches || '',
        warehousesDataView: getWarehousesDataView(state, warehousesParameter),
        session,
    };
};

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadCurrentShipTo,
    loadRetailModeSettings,
    changeCustomerContext: makeHandlerChainAwaitable(changeCustomerContext),
    setFulfillmentMethod,
    updatePickUpWarehouse,
    updateWarehouse,
    loadShipTo,
};

export interface HeaderSignInStyles {
    container?: InjectableCss;
    signedInWrapper?: InjectableCss;
    signedOutWrapper?: InjectableCss;
    loginButtonWrapper?: InjectableCss;
    loginButtonHidden?: HiddenProps;
    loginButtonHiddenMobile?: HiddenProps;
    loginButtonIcon?: IconPresentationProps;
    loginButtonIconMobile?: IconPresentationProps;
    chevronIcon?: IconPresentationProps;
    innerLoginButtonWrapper?: InjectableCss;
    loginText?: TypographyPresentationProps;
    myAccountText?: TypographyPresentationProps;
    popover?: PopoverPresentationProps;
    popoverTitle?: TypographyPresentationProps;
    popoverButtons?: ButtonPresentationProps;
    popoverLinks?: LinkPresentationProps;
    menuItem?: ClickablePresentationProps;
    signOutMenuItem?: ClickablePresentationProps;
    signedInGridContainer?: GridContainerProps;
    signedInInfoGridItem?: GridItemProps;
    signedInMenuGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoSubtitle?: TypographyPresentationProps;
    sectionWrapper?: InjectableCss;
    changeLink?: LinkPresentationProps;
    infoSection?: InjectableCss;
    radioGroup?: RadioGroupProps;
    loadingOverlay?: LoadingOverlayProps;
}

export const styles: HeaderSignInStyles = {
    container: {
        css: css`
            display: inline-flex;
            height: 100%;
        `,
    },
    signedOutWrapper: {
        css: css`
            padding: 2em 2.5em;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding: 1em 12%;
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    loginButtonWrapper: {
        css: css`
            ${({ theme }) => {
                if (isTheme(theme, "horizon")) {
                    return css`
                        color: ${getColor("primary.light")};
                    `;
                }

                return css`
                    color: ${getColor("header.contrastMain")};
                `;
            }}
            height: 100%;
            cursor: pointer;
            ${focusStyles()}
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding: 0 ${MENU.mobileButtonSpacing};
                        `,
                    ],
                    mediaOptions.max,
                )}
            ${({ theme }) => {
                if (isTheme(theme, "horizon")) {
                    return css`
                        &:hover {
                            color: ${getColor("primary.light")};
                            color: ${getColor("header.contrastMain")};
                            ${IconWrapper} {
                                color: ${getColor("primary.light")};
                            }
                        }
                    `;
                }

                return null;
            }}
        `,
    },
    loginButtonHidden: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: center;
        `,
    },
    loginButtonHiddenMobile: {
        css: css`
            display: flex;
        `,
    },
    loginButtonIcon: {
        color: "primary",
        size: 30,
        src: Profile,
        css: css`
            margin-right: 0.5em;
            margin-bottom: 0.1em;
        `,
    },
    loginButtonIconMobile: {
        size: 20,
        src: ProfileMobile,
    },
    chevronIcon: {
        color: "primary",
        size: 15,
        src: ChevronDown,
        css: css`
            position: relative;
            left: 0.2em;
            top: 0.3em;
        `,
    },
    innerLoginButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-right: 0.5em;
        `,
    },
    loginText: {
        color: "primary",
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: ${TYPOGRAPHY.lineHeight.small};
            max-width: 160px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-height: 18px;
        `,
    },
    myAccountText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: ${TYPOGRAPHY.lineHeight.small};
        `,
    },
    popover: {
        wrapperProps: {
            css: css`
                height: 100%;
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: ["static !important", null, null, "relative !important"], properties: ["position"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        contentBodyProps: {
            css: css`
                max-height: none;
                box-shadow: ${getProp("theme.shadows.main")};
                color: inherit;
                color: ${getColor("text")};
                margin-top: 1px;
            `,
        },
    },
    popoverTitle: {
        variant: "h5",
        css: css<any>`
            margin-bottom: 1em;
            text-align: center;
        `,
    },
    popoverLinks: {
        css: css`
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: center;
            color: inherit;
            font-weight: ${TYPOGRAPHY.weight.black};
        `,
    },
    popoverButtons: {
        variant: "secondary",
        css: css`
            width: 100%;
            padding: 0;
            font-size: ${TYPOGRAPHY.size.base};
            ${contentSpacingStyles(null, "0.5em")}
        `,
    },
    signedInWrapper: {},
    signedInGridContainer: { gap: 0 },
    signedInInfoGridItem: {
        width: [12, 8, 8, 8, 8],
        css: css`
            padding: 1.5em;
            font-size: ${TYPOGRAPHY.size.smallest};
            flex-direction: column;
            order: 2;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            order: 1;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    signedInMenuGridItem: {
        width: [12, 4, 4, 4, 4],
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            order: 1;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            order: 2;
                            border-bottom: 0;
                            border-left: 1px solid ${getColor("common.border")};
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    infoContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(["20px"], [TYPOGRAPHY.spacing.base])}
        `,
    },
    infoGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    infoSection: {
        css: css`
            ${contentSpacingStyles(null, "0.75em")}
        `,
    },
    sectionWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 1em;
            margin-bottom: 1em;
            border-bottom: 1px solid ${getColor("common.border")};

            &:last-child {
                padding-bottom: 0;
                margin-bottom: 0;
                border-bottom: 0;
            }
        `,
    },
    infoSubtitle: {
        forwardAs: "p",
        weight: TYPOGRAPHY.weight.bold,
    },
    changeLink: {
        css: css`
            ${({ theme }) => theme?.link?.defaultProps?.css}
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    radioGroup: {
        css: css`
            margin-top: 0.25em;
            ${formVerticalRadioGroupStyles()}
        `,
    },
    loadingOverlay: getLoadingOverlayProps(),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const LoginButton: React.FunctionComponent<Props> = ({
    firstName,
    userName,
    history,
    signInUrl,
    isSignedIn,
    currentBillTo,
    currentShipTo,
    loadCurrentBillTo,
    loadCurrentShipTo,
    loadRetailModeSettings,
    showPickUpOption,
    changeCustomerContext,
    fulfillmentMethod,
    pickUpWarehouse,
    allowCreateAccount,
    allowInternalSignIn,
	rememberMe,
    allowPickupBranchSelection,
    isFullfillmentMethodPickup,
    currentWarehouseName,
    disallowedShippingBranches,
    setFulfillmentMethod,
    warehousesDataView,
    session,
    cart,
    shipToState,
    loadShipTo,
}) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isModalLoading, setIsModalLoading] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const lastBillTo = usePreviousExisting(currentBillTo);
    const lastShipTo = usePreviousExisting(currentShipTo);

    React.useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    const billTo = currentBillTo || lastBillTo;
    const shipTo = shipToState.value || currentShipTo || lastShipTo;

    const displayBillTo = billTo?.customerNumber.substring(billTo?.customerNumber.indexOf('-') + 1);

    const isNotPickupBranchSelectionDisabled = !(!allowPickupBranchSelection && isFullfillmentMethodPickup);
    const disallowedBranchesArray = disallowedShippingBranches.split(',').map(branch => branch.trim());
    const firstShippingAllowedWarehouse = warehousesDataView.value?.find(warehouse => !disallowedBranchesArray.includes(warehouse.name));
    const isBranchPickupOnly = disallowedBranchesArray.includes(currentWarehouseName);

    React.useEffect(() => {
        if (isSignedIn || rememberMe) {
            if (!currentBillTo) {
                loadCurrentBillTo();
            }
            if (!currentShipTo) {
                loadCurrentShipTo();
            }
            if (isBranchPickupOnly && !isFullfillmentMethodPickup) { 
                setPickupAsFulfillmentMethod();
            }
        }
        if (isSignedIn && !rememberMe) {
            loadRetailModeSettings();
        }
    }, []);

    React.useEffect(() => {
        if ((isSignedIn || rememberMe) && isBranchPickupOnly && !isFullfillmentMethodPickup) { 
            setPickupAsFulfillmentMethod();
        }
    }, [currentWarehouseName, warehousesDataView.value]);

    const element = React.useRef<HTMLUListElement>(null);
    const popover = React.useRef<HTMLUListElement>(null);

    const onSignInHandler = () => {
        if (!isSignedIn) {
            closePopover();
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                history.push(signInUrl);
            }
        }
    };

    const onCreateAccountHandler = () => {
        if (!isSignedIn) {
            history.push("/MyAccount/CreateAccount");
            closePopover();
        }
    };

    const closePopover = () => {
        popover.current && (popover.current! as any).closePopover();
    };

    const handleChangeFulfillmentMethod = async (event: ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        await changeCustomerContext({ fulfillmentMethod: event.currentTarget.value, pickUpWarehouse });
    };

    const setPickupAsFulfillmentMethod = async () => {
        setIsLoading(true);
        
        await setFulfillmentMethod({
            fulfillmentMethod: FulfillmentMethod.PickUp,
            onComplete: () => {
                setIsLoading(false);
            },
        });
        changeShippingWarehouseIfPickupOnly();
    }

    const changeShippingWarehouseIfPickupOnly = () => {
        if (!firstShippingAllowedWarehouse) {
            return;
        }
        updateWarehouse({ warehouse: firstShippingAllowedWarehouse });
    }

    const changeWarehouse = () => {
        setIsModalOpen(true);
        closePopover();
    };

    const closeModal = () => setIsModalOpen(false);
    const selectWarehouse = async (pickUpWarehouse: WarehouseModel) => {
        setIsModalLoading(true);
        await changeCustomerContext({ fulfillmentMethod, pickUpWarehouse });
    };

    const addReturnUrlParameter = () => {
        const pageUrl = window.location.pathname;
        const parameters = window.location.search;
        const semiEncodedParameters = parameters.replace(/&/g, '%26');
        const hasReturnUrl = parameters.includes("returnUrl");
        if (!isSignedIn && !rememberMe && !hasReturnUrl) {
            history.push(`${pageUrl}${parameters ? `${parameters}&` : "?"}returnUrl=${pageUrl}${semiEncodedParameters}`);
        }
    };

    const accountMenuProps = {
        onClick: closePopover,
    };

    const signInExternalProvidersProps: SignInExternalProvidersPartialProps = {
        fields: {
            isBoxed: false,
            fullWidthButton: true,
        },
        extendedStyles: {
            providerButton: {
                color: "secondary",
                css: css`
                    width: 100%;
                `,
            },
        },
        overrideStyles: {
            container: {
                css: css`
                    ${contentSpacingStyles(null, "0.5em")}
                `,
            },
        },
        infosBoxProps: {
            fields: {
                showIcon: false,
            },
            extendedStyles: {
                wrapper: {
                    css: css`
                        text-align: center;
                    `,
                },
            },
        },
    };

    const loginButton = (
        <Clickable {...styles.loginButtonWrapper}>
            <Hidden {...styles.loginButtonHidden} below={isSignedIn && isNotPickupBranchSelectionDisabled ? "lg" : "md"}>
                <Icon {...styles.loginButtonIcon} />
                <StyledWrapper {...styles.innerLoginButtonWrapper}>
                    <Typography {...styles.myAccountText}>{translate("My Account")}</Typography>
                    <Typography {...styles.loginText}>
                        {isSignedIn ? `${displayBillTo || ""}` : translate("Log In")}
                        <Icon {...styles.chevronIcon} />
                    </Typography>
                </StyledWrapper>
            </Hidden>
            <Hidden {...styles.loginButtonHiddenMobile} above={isSignedIn && isNotPickupBranchSelectionDisabled ? "md" : "sm"}>
                <Icon {...styles.loginButtonIconMobile} />
            </Hidden>
        </Clickable>
    );

    return (
        <StyledWrapper {...styles.container}>
            <Popover
                {...styles.popover}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                    ref: element,
                    css: css`
                        ${styles.popover?.contentBodyProps?.css}
                        width: 100%;
                        right: 0;
                        ${props => {
                            if (isSignedIn) {
                                return breakpointValuesMediaQueries(
                                    props.theme,
                                    [{ values: [null, null, "70%", "700px"], properties: ["width"] },
                                    { values: [null, null, "15%", "0"], properties: ["right"] }],
                                    mediaOptions.min,
                                );
                            }

                            return breakpointValuesMediaQueries(
                                props.theme,
                                [{ values: [null, null, "285px"], properties: ["width"] }],
                                mediaOptions.min,
                            );
                        }}
                    `,
                }}
                positionFunction={() => {
                    return {
                        position: "absolute",
                    };
                }}
                ref={popover}
                insideRefs={[element]}
                popoverTrigger={loginButton}
				onOpen={addReturnUrlParameter}
            >
                {(isSignedIn || rememberMe) ? (
                    <StyledWrapper {...styles.signedInWrapper}>
                        <GridContainer {...styles.signedInGridContainer}>
                            <GridItem {...styles.signedInMenuGridItem}>
                                <AccountListMenu {...accountMenuProps}/>
                            </GridItem>
                            <GridItem {...styles.signedInInfoGridItem}>
                                <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                                    <LoginButtonCheckboxDisplay/>
                                    <StyledWrapper {...styles.sectionWrapper}>
                                        <GridContainer {...styles.infoContainer}>
                                            {billTo && (
                                                <GridItem {...styles.infoGridItem}>
                                                    {billTo && (
                                                        <StyledWrapper {...styles.infoSection}>
                                                            <Typography {...styles.infoSubtitle}>
                                                                {translate("Billing Address")}
                                                            </Typography>
                                                            <AddressInfoDisplay
                                                                {...billTo}
                                                                state={billTo.state?.abbreviation}
                                                                country={billTo.country?.abbreviation}
                                                                erpNumber={billTo.properties?.erpNumber}
                                                            />
                                                        </StyledWrapper>
                                                    )}
                                                </GridItem>
                                            )}
                                            {shipTo && (
                                                <GridItem {...styles.infoGridItem}>
                                                    <Typography {...styles.infoSubtitle}>
                                                        {translate("Shipping Address")}
                                                    </Typography>
                                                    <AddressInfoDisplay
                                                        {...shipTo}
                                                        state={shipTo.state?.abbreviation}
                                                        country={shipTo.country?.abbreviation}
                                                        erpNumber={shipTo.properties?.erpNumber}
                                                        jobNumber={shipTo.properties?.jobNumber}
                                                        taxCode={shipTo.properties?.shipToTaxCode}
                                                    />
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.sectionWrapper}>
                                        <GridContainer {...styles.infoContainer}>
                                            <GridItem {...styles.infoGridItem}>
                                                <Typography {...styles.infoSubtitle}>
                                                    {translate("Fulfillment Method")}
                                                </Typography>
                                                <RadioGroup
                                                    {...styles.radioGroup}
                                                    value={fulfillmentMethod}
                                                    onChangeHandler={handleChangeFulfillmentMethod}
                                                >
                                                    <Radio value={FulfillmentMethod.Ship} disabled={!showPickUpOption || isBranchPickupOnly}>
                                                        {translate("Ship")}
                                                    </Radio>
                                                    {showPickUpOption && (
                                                        <Radio value={FulfillmentMethod.PickUp}>
                                                            {translate("Pick Up")}
                                                        </Radio>
                                                    )}
                                                </RadioGroup>
                                            </GridItem>
                                            <GridItem {...styles.infoGridItem}>
                                                {fulfillmentMethod === FulfillmentMethod.PickUp ? (
                                                    <>
                                                        <Typography {...styles.infoSubtitle}>
                                                            {translate("Pick Up Branch")}
                                                        </Typography>
                                                        {pickUpWarehouse ? (
                                                            <>
                                                                <Typography>
                                                                    {pickUpWarehouse.name} -
                                                                    {pickUpWarehouse.description}
                                                                </Typography>
                                                                <AddressInfoDisplay {...pickUpWarehouse} />
                                                            </>
                                                        ) : null}
                                                        {allowPickupBranchSelection && (
                                                            <Link onClick={changeWarehouse} {...styles.changeLink}>
                                                                {translate("Change")}
                                                            </Link>
                                                        )}
                                                    </>
                                                ) : null}
                                            </GridItem>
                                        </GridContainer>
                                    </StyledWrapper>
                                </LoadingOverlay>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.signedOutWrapper}>
                        <Typography {...styles.popoverTitle}>{translate("Sign In or Create Account")}</Typography>
                        {allowInternalSignIn && (
                            <Button {...styles.popoverButtons} onClick={onSignInHandler}>
                                {translate("Sign In")}
                            </Button>
                        )}
                        {!allowInternalSignIn ? <SignInExternalProviders {...signInExternalProvidersProps} /> : null}
                        {allowCreateAccount && (
                            <Button buttonType="outline" {...styles.popoverButtons} onClick={onCreateAccountHandler}>
                                {translate("Create Account")}
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </Popover>
            {isSignedIn ? (
                <FindLocationModal
                    modalIsOpen={isModalOpen}
                    onWarehouseSelected={selectWarehouse}
                    onModalClose={closeModal}
                    modalIsLoading={isModalLoading}
                />
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(LoginButton)),
    definition: {
        group: "Common",
        displayName: "Sign In Button",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
            },
            {
                name: fields.showRootPage,
                displayName: "Show selected page in menu",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showSignOut,
                displayName: 'Show "Sign Out" button',
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
				getDisplay: (item: HasFields, state) => {
					const {
                        LinkField: { type, value },
                        linkName,
						isCategoryTitle,
						categoryTitle,
                    } = item.fields;

					if(isCategoryTitle === "Category" && categoryTitle) {
						return `${categoryTitle} (category name)`;
					}
					if(isCategoryTitle === "Link") {
						const link = getPageLinkByNodeId(state, value);
						return `${linkName || link?.title} (link)`;
					}
					return value;
				},
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "isCategoryTitle",
                        displayName: "type of field",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Category Name", value: "Category" },
                            { displayName: "Link Field", value: "Link" },
                        ],
                        defaultValue: "Category",
                        hideEmptyOption: true,
                    },
                    {
                        name: "categoryTitle",
                        displayName: "Category Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.isCategoryTitle === "Category",
                    },
                    {
                        name: "LinkField",
                        displayName: "Link Field",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: widget => widget.fields.isCategoryTitle === "Link",
                    },
                    {
                        name: "linkName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.isCategoryTitle === "Link",
                    },
                    {
                        name: "showOnlyAdmin",
                        displayName: 'Displayed to Admin users only',
                        editorTemplate: "CheckboxField",
                        isVisible: widget => widget.fields.isCategoryTitle === "Link",
                        defaultValue: false,
                    },
                    {
                        name: "showOnlyAdminBuyer3",
                        displayName: 'Displayed to Admin and Buyer3 users only',
                        editorTemplate: "CheckboxField",
                        isVisible: widget => widget.fields.isCategoryTitle === "Link",
                        defaultValue: false,
                    },
                    {
                        name: "hideForCanadianCustomers",
                        displayName: 'Hidden to Canadian users',
                        editorTemplate: "CheckboxField",
                        isVisible: widget => widget.fields.isCategoryTitle === "Link",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
